import { last } from 'lodash-es';
import { Observable } from 'rxjs';
import { CustomEventType, type UnauthorizedEventDetail } from '../tokens/event';
import type { DataResponse, ErrorResponse } from '../types/rest';
import { eraseCookie, readCookie } from './cookies';
import { envConfig } from './envConfig';
import { checkHeadersForMFARequired, handleMFAFlow } from './http.mfa';
import { FetchError, type RequestOptions } from './http.types';
import { logger } from './logger';

export const NETWORK_ERROR_FINGERPRINT = 'network_error';
export class NetworkError extends Error {
  url: string;
  // setting datadog fingerprint to ensure these errors are grouped together (this is a magic field name "dd_fingerprint" that datadog looks for)
  // https://docs.datadoghq.com/tracing/error_tracking/error_grouping/?tab=browser#example-2
  dd_fingerprint = NETWORK_ERROR_FINGERPRINT;

  constructor(message: string, url: string) {
    super(`${message}: ${url}`);
    this.url = url;
  }
}

function createFormData(obj: object | null) {
  const data: string[] = [];
  for (const key in obj) {
    data.push(encodeURIComponent(key) + '=' + encodeURIComponent(obj[key as keyof typeof obj]));
  }
  return data.join('&');
}

// Wrapper around performRequestInner to do pagination handling
function performRequest<TResponse = any>(
  endpoint: string,
  path: string,
  { paginateRecords, onPageData, ...restOfOptions }: RequestOptions = {}
): Promise<TResponse> {
  if (paginateRecords) {
    const baseUrl = `${endpoint}${path}`;
    const pagedResponse: Promise<DataResponse<any>> = restFetchPagesUpToLimit(
      baseUrl,
      paginateRecords,
      (dynamicUrl: string) => performRequestInner(dynamicUrl, restOfOptions),
      onPageData
    );
    return pagedResponse as Promise<TResponse>;
  } else {
    return performRequestInner(`${endpoint}${path}`, restOfOptions);
  }
}

/* CSRF Token handling
 * We need to send the CSRF Token with every non-idempotent (e.g. POST / PUT / DELETE / PATCH)
 * request we make.
 * We're working on the assumption that before making any non-idempotent request, we'll have first
 * made e.g. a `GET` request.  For every request that we make, if it has the `X-CSRF-Token` header,
 * we'll save that and use it for the next request we make.
 * Technically, we only need to send the token for CSRF Protected endpoints (non-idempotent ones),
 * but sending it every time is just easier to implement.
 * Also, for CORS requests, we need the backend to send `Access-Control-Allow-Headers: X-CSRF-Token`
 * and also `Access-Control-Expose-Headers: X-CSRF-Token` (otherwise we won't be able to read that header
 * on any CORS responses).
 * NOTE: CSRF Tokens are unique per origin, hence the map of Origin -> Token.
 */
const csrfTokenMap = new Map<string, string>();
const csrfTokenName = 'X-Csrf-Token';

/* UI Channel handling
 * If we have no UI channel cookie set, the Nginx proxy will serve files from some default container.
 * When we receive the first response from the back-end, we do get a value for the cookie, and
 * if that value differs from the default setting, we want to reload the page.
 *
 * This is to make sure that all assets come from the same container.
 */
const uiChannelCookieName = 'ui_channel';
const uiChannelValue = readCookie(uiChannelCookieName);
let isFirstRequest = true;

async function performRequestInner<TResponse = any>(url: string, options: RequestOptions): Promise<TResponse> {
  const requestUrl = new URL(url, window.location.origin);
  const hadCsrfTokenBeforeRequest = csrfTokenMap.has(requestUrl.origin);
  options.headers = {
    ...(options.headers || {}),
    'X-Talos-UI-Version': import.meta.env.VITE_GIT_HASH,
    ...(csrfTokenMap.has(requestUrl.origin) ? { [csrfTokenName]: csrfTokenMap.get(requestUrl.origin) } : {}),
  };
  // Must use `requestUrl.toString()` here because MSW doesn't like passing in a `URL` instance
  return fetch(requestUrl.toString(), options)
    .then(async response => {
      if (response.headers.has(csrfTokenName)) {
        // Save the CSRF Token if we can
        csrfTokenMap.set(requestUrl.origin, response.headers.get(csrfTokenName) ?? '');
      }
      if (response.status === 204) {
        return null;
      }

      if (isFirstRequest && uiChannelValue !== readCookie(uiChannelCookieName) && envConfig.VITE_AVA_ENV !== 'test') {
        window.location.reload();
      } else {
        isFirstRequest = false;
      }

      const body: any = await response.text();
      let json: TResponse | ErrorResponse | undefined;

      try {
        json = JSON.parse(body);
      } catch (e) {
        // Could not parse JSON, assume it's plain text
      }

      if (response.ok) {
        return json ?? body;
      }

      throw new FetchError({
        response,
        body,
        errorJson: json as ErrorResponse,
        url,
        options,
      });
    })
    .catch(async (e: FetchError | TypeError) => {
      if ('response' in e) {
        // if the request needs authcode, initiate the authcode flow
        if (checkHeadersForMFARequired(e)) {
          return handleMFAFlow({
            e,
            options,
            retryRequestOnceValidated: updatedOptions => performRequestInner(url, updatedOptions),
          });
        }

        // this is a FetchError
        if (e.response.status === 401) {
          eraseCookie(envConfig.VITE_SESSION_COOKIE);

          document.dispatchEvent(
            new CustomEvent<UnauthorizedEventDetail>(CustomEventType.HTTP_UNAUTHORIZED, {
              detail: { error: e },
            })
          );
        }
        if (e.response.status === 403 && !hadCsrfTokenBeforeRequest && csrfTokenMap.has(requestUrl.origin)) {
          // Possibly an error caused by a missing CSRF token
          // Since we didn't have one before, but we got one in the error response, we'll retry the request
          // and this time it will include the new CSRF token
          return performRequestInner(url, options);
        }
        // Don't log auth errors. Additionally, allow skipping error reporting for other status codes
        const skippedErrorCodes = [401, 403, ...(options.skippedErrorReportingStatusCodes || [])];
        if (!skippedErrorCodes.includes(e.response.status)) {
          const extra = typeof options?.body === 'object' ? { body: options.body } : undefined;
          logger.error(e, {
            url,
            response: { body: e.body, status: e.response.status },
            // include the request body in the error log
            extra,
          });
        }
        return Promise.reject(e);
      }
      return Promise.reject(new NetworkError(e.message, url));
    });
}

export function Get<TResponse = any>(endpoint: string, path = '', options: RequestOptions = {}): Promise<TResponse> {
  return performRequest(endpoint, path, {
    credentials: 'include',
    ...options,
  });
}

export function Head<TResponse = any>(endpoint: string, path = '', options: RequestOptions = {}): Promise<TResponse> {
  return performRequest(endpoint, path, {
    credentials: 'include',
    method: 'HEAD',
    ...options,
  });
}

const isContentTypeMatching = (contentType: HeadersInit | undefined, matchType: 'json' | 'formUrlEncoded') => {
  const typeToMatch = matchType === 'json' ? 'application/json' : 'application/x-www-form-urlencoded';
  return (contentType as Extract<HeadersInit, Record<string, string>> | undefined)?.['Content-Type'] === typeToMatch;
};

export function Post<TResponse = any>(
  endpoint: string,
  path: string,
  payload: object | null = {},
  options: RequestOptions = {}
): Promise<TResponse> {
  return performRequest(endpoint, path, {
    method: 'post',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: isContentTypeMatching(options?.headers, 'json') ? JSON.stringify(payload) : createFormData(payload),
    ...options,
  });
}

export function Put<TResponse = any>(
  endpoint: string,
  path: string,
  payload: object | null = {},
  options: RequestOptions = {}
): Promise<TResponse> {
  return performRequest(endpoint, path, {
    method: 'put',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: isContentTypeMatching(options?.headers, 'json') ? JSON.stringify(payload) : createFormData(payload),
    ...options,
  });
}

export function Patch<TResponse = any>(
  endpoint: string,
  path: string,
  payload: object | null = {},
  options: RequestOptions = {}
): Promise<TResponse> {
  return performRequest(endpoint, path, {
    method: 'PATCH',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: isContentTypeMatching(options?.headers, 'formUrlEncoded') ? createFormData(payload) : JSON.stringify(payload),
    ...options,
  });
}

export function Delete<TResponse = any>(
  endpoint: string,
  path: string,
  payload: object | null = {},
  options: RequestOptions = {}
): Promise<TResponse> {
  return performRequest(endpoint, path, {
    method: 'delete',
    credentials: 'include',
    body: JSON.stringify(payload),
    ...options,
  });
}

export const GET = 'GET';
export const POST = 'POST';
export const PUT = 'PUT';
export const PATCH = 'PATCH';
export const DELETE = 'DELETE';

/**
 * Send a JSON request.
 * @param method One of GET, POST, PUT, PATCH, or DELETE
 * @param url URL to which the JSON call should be sent
 * @param payload Non-stringified JSON (GET requests do not accept a payload)
 * @param requestOptions Additional options to pass to the fetch call
 */
export async function request<TResponse = any>(
  method: typeof GET | typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string
): Promise<TResponse>;
export async function request<TResponse = any>(
  method: typeof GET | typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string,
  payload: null,
  requestOptions?: RequestOptions
): Promise<TResponse>;
export async function request<TResponse = any>(
  method: typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string,
  payload: object | null,
  requestOptions?: RequestOptions
): Promise<TResponse>;
export async function request<TResponse = any>(
  method: typeof GET | typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string,
  payload: object | null = null,
  requestOptions: RequestOptions = {}
): Promise<TResponse> {
  const options: RequestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
    ...requestOptions,
  };
  if (payload != null) {
    if (method === GET) {
      throw new Error(`Payload must be empty when using ${GET}`);
    }
    options.body = JSON.stringify(payload);
  }
  switch (method) {
    case GET:
      return Get(url, '', options);
    case POST:
      return Post(url, '', null, options);
    case PUT:
      return Put(url, '', null, options);
    case PATCH:
      return Patch(url, '', null, options);
    case DELETE:
      return Delete(url, '', null, options);
    default:
      throw new Error('Unsupported HTTP method');
  }
}

/**
 * Send a JSON request.
 * @param method One of GET, POST, PUT, PATCH, or DELETE
 * @param url URL to which the JSON call should be sent
 */
export function requestObservable<TResponse = any>(
  method: typeof GET | typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string
): Observable<TResponse>;

/**
 * Send a JSON request.
 * @param method One of GET, POST, PUT, PATCH, or DELETE
 * @param url URL to which the JSON call should be sent
 * @param payload Non-stringified JSON (GET requests do not accept a payload)
 */
export function requestObservable<TResponse = any>(
  method: typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string,
  payload: object
): Observable<TResponse>;

/**
 * Send a JSON request.
 * @param method One of GET, POST, PUT, PATCH, or DELETE
 * @param url URL to which the JSON call should be sent
 * @param payload Non-stringified JSON (GET requests do not accept a payload)
 */
export function requestObservable<TResponse = any>(
  method: typeof GET | typeof POST | typeof PUT | typeof PATCH | typeof DELETE,
  url: string,
  payload: object | null = null
): Observable<TResponse> {
  const abortController = new AbortController();
  const options: RequestInit = {
    headers: {
      'Content-Type': 'application/json',
    },
    signal: abortController.signal,
  };
  if (payload != null) {
    if (method === GET) {
      throw new Error(`Payload must be empty when using ${GET}`);
    }
    options.body = JSON.stringify(payload);
  }
  return new Observable(function subscribe(subscriber) {
    let result: Promise<TResponse>;
    switch (method) {
      case GET:
        result = Get(url, '', options);
        break;
      case POST:
        result = Post(url, '', null, options);
        break;
      case PUT:
        result = Put(url, '', null, options);
        break;
      case PATCH:
        result = Patch(url, '', null, options);
        break;
      case DELETE:
        result = Delete(url, '', null, options);
        break;
      default:
        throw new Error('Unsupported HTTP method');
    }
    result
      .then(response => subscriber.next(response))
      .then(() => subscriber.complete())
      .catch(err => subscriber.error(err));
    return function unsubscribe() {
      abortController.abort('Observable unsubscribed');
    };
  });
}

/**
 * A Promise-based REST paginator tool that fetches pages up to a specified limit.
 *
 * The resolved promise will contain the merged data from all fetched pages. If the limit was reached while more data
 * was available, the 'next' property will be preserved from the last response to indicate more data exists.
 *
 * @param baseUrl The standard request url (not applying any ?after= query)
 * @param limit pagination limit (max records)
 * @param paginatableRequestFn A wrapped request function which accepts a request with the after query param set
 */
export async function restFetchPagesUpToLimit<T>(
  baseUrl: string,
  limit: number,
  paginatableRequestFn: (paginatedUrl: string) => Promise<DataResponse<T>>,
  onPageData?: (cumulativeData: T[]) => void
) {
  const responses: DataResponse<T>[] = [];
  let url: string | null = baseUrl;

  while (url) {
    const response = await paginatableRequestFn(url);
    if (!isDataResponse(response)) {
      throw new Error('Received an invalid REST response type for performing pagination');
    }
    responses.push(response);
    const recordsReceived = responses.reduce((sum, r) => sum + r.data.length, 0);
    if (response.next && recordsReceived < limit) {
      onPageData?.(responses.flatMap(r => r.data));
      url = attachAfterSearchParam(baseUrl, response.next);
    } else {
      url = null;
    }
  }

  const lastResponse = last(responses);
  return {
    ...responses[0],
    next: lastResponse?.next,
    data: responses.flatMap(response => response.data),
  };
}

function attachAfterSearchParam(baseUrl: string, afterValue: string): string {
  // Org API endpoints are protocol-relative (`//host/v1/...`). `new URL(baseUrl)` alone
  // rejects those; resolve against the page origin the same way performRequestInner does.
  const url =
    typeof window !== 'undefined' && window.location?.origin
      ? new URL(baseUrl, window.location.origin)
      : new URL(baseUrl);
  url.searchParams.set('after', afterValue);
  return url.href;
}

function isDataResponse<T = any>(response: unknown): response is DataResponse<T> {
  return !!(response && response instanceof Object && 'data' in response && response['data'] instanceof Array);
}
